import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';

import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Button from '@adsk/bim360-matrix-react-button';
import MultiSelectList from '@adsk/bim360-matrix-react-multi-select-list';

import './_ListSelectionModal.scss';

export class ListSelectionModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.getItems(props),
      searchExpression: props.searchExpression || ''
    };

    this.clearSelectedItems = this.clearSelectedItems.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSearchClear = this.onSearchChange.bind(this, '');
    this.onCheckChange = this.onCheckChange.bind(this);
    this.cancelClick = this.cancelClick.bind(this);
    this.onConfirm = this.onConfirm.bind(this);
    this.onExitModal = this.onExitModal.bind(this);
    this.onHideModal = this.onHideModal.bind(this);
  }

  componentDidMount() {
    this.state.searchExpression && this.onSearchChange();
  }

  componentDidUpdate(prevProps) {
    let shouldUpdateSearch;
    const newSearchExpression = this.isControlled('searchExpression')
      ? this.props.searchExpression || ''
      : this.state.searchExpression;

    if (
      this.props.selectedItems !== prevProps.selectedItems ||
      this.props.defaultSelectedItems !== prevProps.defaultSelectedItems ||
      this.props.items !== prevProps.items
    ) {
      // Get updated items from props
      this.setState(this.getItems(this.props));

      if (newSearchExpression.length > 0) {
        shouldUpdateSearch = true;
      }
    }

    // Support managed searchExpression
    if (newSearchExpression !== this.state.searchExpression) {
      this.setState({ searchExpression: newSearchExpression }, this.onSearchChange);
    } else if (shouldUpdateSearch) {
      this.onSearchChange(newSearchExpression);
    }
  }

  getItems(props = this.props) {
    const selectedItems = this.isControlled('selectedItems', props)
      ? props.selectedItems
      : props.defaultSelectedItems;
    const selectedItemValues = selectedItems.map(item => this.getValue(item));

    const { items, grouped } = props;

    return {
      selectedItems,
      initialSelectedItems: selectedItems,
      items: grouped
        ? /* When groups are involved, item grouping is managed externally by the calling
          Component and so we don't separate them here to top / bottom groups. */
        items
        : // When no groups are involved, item grouping top / bottom is managed internally.
        items.filter(item => !selectedItemValues.includes(this.getValue(item)))
    };
  }

  /**
   * returns true if prop exists in props (i.e. - supplied in the props), false otherwise
   */
  isControlled(prop, props = this.props) {
    return prop in props;
  }

  getLabel(option, props = this.props) {
    return this.props.labelGetter(option, props);
  }

  getValue(option, props = this.props) {
    return this.props.valueGetter(option, props);
  }

  filterItems(items, searchExpression) {
    const { filterOptions } = this.props;

    if (filterOptions) {
      return filterOptions(items, searchExpression);
    }

    const searchExpressionLowerCased = searchExpression.toLowerCase();
    return items.filter(
      item =>
        this.getLabel(item)
          .toLowerCase()
          .indexOf(searchExpressionLowerCased) > -1
    );
  }

  applyFilter(items, searchExpression, grouped) {
    if (!grouped) {
      return this.filterItems(items, searchExpression);
    }

    return items.map(group => ({
      ...group,
      options: this.filterItems(group.options, searchExpression)
    }));
  }

  clearSelectedItems() {
    if (!this.isControlled('selectedItems')) {
      this.setState({ selectedItems: []});
    }
    this.props.onChange([]);
    this.props.onClear();
  }

  cancelClick() {
    this.props.onCancel();
  }

  onConfirm() {
    this.props.onConfirm(
      this.isControlled('selectedItems') ? this.props.selectedItems : this.state.selectedItems
    );
  }

  onSearchChange(searchExpression = this.state.searchExpression, props = this.props) {
    const { grouped } = props;
    const { items, selectedItems } = this.getItems(props);

    const filteredItems =
      searchExpression.length > 0 ? this.applyFilter(items, searchExpression, grouped) : items;

    let initialSelectedItems = [];

    // When grouped, the items are already separated into groups and so no top / bottom
    // separation is needed at this point.
    if (!grouped) {
      initialSelectedItems =
        searchExpression.length > 0
          ? this.filterItems(selectedItems, searchExpression)
          : selectedItems;
    }

    const newState = {
      searchExpression,
      items: filteredItems,
      initialSelectedItems
    };
    this.setState(newState);

    this.props.onSearchExpressionChange && this.props.onSearchExpressionChange(searchExpression);
  }

  onCheckChange(currentSelectOptions, option, selectedStatus) {
    let updatedSelected = [];

    // Filter out the option in case it's deselected
    if (!selectedStatus) {
      updatedSelected = this.state.selectedItems.filter(
        item => this.getValue(item) !== this.getValue(option)
      );
    } else {
      // Add the option in case it was selected
      updatedSelected = [...this.state.selectedItems, option];
    }

    // Only if we're in uncontrolled mode, update state.
    if (!this.isControlled('selectedItems')) {
      this.setState({ selectedItems: updatedSelected });
    }

    // Call onChange with the full updated selected items
    this.props.onChange && this.props.onChange(updatedSelected, option, selectedStatus);
  }

  _renderModalFooter() {
    const { clearButtonText, confirmButtonText, isConfirmButtonDisabled } = this.props;

    return (
      <div className="ListSelectionModal__footer">
        <div className="ListSelectionModal__footer-left">
          {this.state.selectedItems.length > 0 && (
            <Button
              styleType={Button.StyleType.INLINE_LINK}
              onClick={this.clearSelectedItems}
              className="ListSelectionModal__clear-items"
              data-testid="ListSelectionModal__clear-items"
            >
              {clearButtonText || this.props[TSymbolProp].translate('CLEAR_ITEMS')}
            </Button>
          )}
        </div>
        <div className="ListSelectionModal__footer-right">
          <Button
            styleType={Button.StyleType.LINK_SECONDARY}
            onClick={this.cancelClick}
            className="ListSelectionModal__cancel-button"
            data-testid="ListSelectionModal__cancel-button"
          >
            {this.props[TSymbolProp].translate('CANCEL')}
          </Button>
          <Button
            styleType={Button.StyleType.PRIMARY}
            onClick={this.onConfirm}
            className="ListSelectionModal__confirm-button"
            data-testid="ListSelectionModal__confirm-button"
            disabled={isConfirmButtonDisabled}
          >
            {confirmButtonText || this.props[TSymbolProp].translate('CONFIRM')}
          </Button>
        </div>
      </div>
    );
  }

  _renderEmptyState() {
    const {
      onEmptyStateButtonClick,
      emptyStateMessage,
      emptyStateTitle,
      onNotFoundButtonClick,
      notFoundMessage,
      notFoundTitle,
      onCreateNew
    } = this.props;

    const isSearching = this.state.searchExpression.length > 0;
    const showButton = !isSearching || notFoundMessage;
    const defaultButtonOnClick = onEmptyStateButtonClick || onCreateNew;
    const onButtonClick = isSearching
      ? onNotFoundButtonClick || defaultButtonOnClick
      : defaultButtonOnClick;

    const title = isSearching
      ? notFoundTitle || this.props[TSymbolProp].translate('NOT_FOUND')
      : emptyStateTitle || this.props[TSymbolProp].translate('NO_ITEMS');

    const emptyStateIcon = isSearching
      ? { ...EmptyState.Icons.NOT_FOUND, height: '100px', width: '240px' }
      : { ...EmptyState.Icons.EMPTY_BOX, height: '110px', width: '86px' };

    const text = (
      <div className="ListSelectionModal__empty-state-text">
        <div className="ListSelectionModal__empty-state-text-line">{title}</div>
        {showButton && (
          <div className="ListSelectionModal__empty-state-text-line">
            <Button
              data-testid="ListSelectionModal__empty-state-button"
              className="ListSelectionModal__empty-state-button"
              styleType={Button.StyleType.INLINE_LINK}
              onClick={onButtonClick}
            >
              {isSearching
                ? notFoundMessage
                : emptyStateMessage || this.props[TSymbolProp].translate('CREATE_NEW')}
            </Button>
          </div>
        )}
      </div>
    );

    return (
      <EmptyState
        icon={emptyStateIcon}
        className="ListSelectionModal__empty-state"
        text=""
        footer={text}
      />
    );
  }

  _renderMultiSelectList() {
    const { valueGetter, labelGetter, multiSelectListProps, grouped } = this.props;
    const { items, selectedItems, initialSelectedItems } = this.state;

    return (
      <MultiSelectList
        {...multiSelectListProps}
        grouped={grouped}
        options={items}
        topOptions={initialSelectedItems}
        selectedOptions={selectedItems}
        labelGetter={labelGetter}
        valueGetter={valueGetter}
        onChange={this.onCheckChange}
        className="ListSelectionModal__list"
      />
    );
  }

  _renderModalBody() {
    const { items, initialSelectedItems } = this.state;
    const { grouped } = this.props;

    const isEmpty = grouped
      ? // If we have at least a single option in one of the groups, it's not empty
      items.every(group => !group.options.length)
      : // When not in grouped more, just check the length
      !(items.length || (initialSelectedItems && initialSelectedItems.length));

    return isEmpty ? this._renderEmptyState() : this._renderMultiSelectList();
  }

  onExitModal() {
    this.setState({ searchExpression: '' });
    this.props.onExited && this.props.onExited();
  }

  onHideModal() {
    this.setState({ searchExpression: '' });
    this.props.onHide && this.props.onHide();
  }

  render() {
    const {
      items,
      title,
      show,
      container,
      searchBoxPlaceholder,
      onCreateNew,
      secondaryLinkProps,
      searchBoxProps
    } = this.props;
    const searchDisabled = items.length === 0;

    return (
      <SimpleModal
        title={title}
        show={show}
        footer={this._renderModalFooter()}
        onHide={this.onHideModal}
        onExited={this.onExitModal}
        className="ListSelectionModal"
        container={container}
      >
        <div className="ListSelectionModal__search-bar">
          <div className="ListSelectionModal__search-bar-text-box">
            <SearchBox
              {...searchBoxProps}
              placeholder={searchBoxPlaceholder || this.props[TSymbolProp].translate('SEARCH')}
              searchExpression={this.state.searchExpression}
              onChange={this.onSearchChange}
              onClear={this.onSearchClear}
              onSearch={() => {}}
              className="ListSelectionModal__search-box"
              inputDisabled={searchDisabled}
              searchDisabled={searchDisabled}
            />
            {onCreateNew ? (
              <Button
                onClick={onCreateNew}
                className="ListSelectionModal__create-new-button"
                data-testid="ListSelectionModal__create-new-button"
              >
                {this.props[TSymbolProp].translate('CREATE_NEW')}
              </Button>
            ) : null}
          </div>
          {secondaryLinkProps && secondaryLinkProps.onClick ? (
            <div className="ListSelectionModal__search-bar-secondary-link">
              <Button styleType={Button.StyleType.INLINE_LINK} onClick={secondaryLinkProps.onClick}>
                {secondaryLinkProps.text}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="ListSelectionModal__list-container">{this._renderModalBody()}</div>
      </SimpleModal>
    );
  }
}

ListSelectionModal.defaultProps = {
  onCancel: noop,
  onHide: noop,
  onExited: noop,
  onConfirm: noop,
  onChange: noop,
  onClear: noop,
  items: [],
  defaultSelectedItems: [],
  valueKey: 'value',
  labelKey: 'label',
  valueGetter: (option, props) => option[props.valueKey],
  labelGetter: (option, props) => option[props.labelKey]
};

ListSelectionModal.propTypes = {
  /**
   * List of items, grouped as objects.
   * Each object in the array represents a group and must contain 'title' and list options.
   * Also, an optional 'groupId' is supported in order for the (also optional)
   * MultiSelectList groupHeaderActionRenderer to identify which group header it's rendering.
   *
   * A plain non-grouped list of items is also supported. In that case, each item in the array
   * should contain a 'labelKey' prop and a 'valueKey' prop.
   */
  items: MultiSelectList.propTypes.options,
  /**
   * List of selected item values
   */
  defaultSelectedItems: PropTypes.arrayOf(PropTypes.object),
  /**
   * List of selected item values - makes the component controlled
   */
  selectedItems: PropTypes.arrayOf(PropTypes.object),
  /**
   * Item label key
   */
  labelKey: PropTypes.string,
  /**
   * Item value key
   */
  valueKey: PropTypes.string,
  /**
   * () => {}
   * Callback that's called when the modal closes
   */
  onExited: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the modal hides
   */
  onHide: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the modal confirm button is clicked
   */
  onConfirm: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the modal items selection status has been changed
   */
  onChange: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the modal cancel button is clicked
   */
  onCancel: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the items list has been cleared
   */
  onClear: PropTypes.func,
  /**
   * () => {}
   * Callback that's called when the empty state button / create new button is clicked
   */
  onCreateNew: PropTypes.func,
  /**
   * A container to attach the modal to
   */
  container: PropTypes.object,
  /**
   * Whether or not to show the modal
   */
  show: PropTypes.bool.isRequired,
  /**
   * The modal title
   */
  title: PropTypes.string,
  /**
   * Selector function for getting the label of an option
   */
  labelGetter: PropTypes.func,
  /**
   * Selector function for getting the value of an option
   */
  valueGetter: PropTypes.func,
  /**
   * Optional extra Props to be passed onto MultiSelectList inner component
   */
  multiSelectListProps: PropTypes.shape(MultiSelectList.propTypes),
  /**
   * Indicates whether the items passed were in the shape of groups or plain list
   */
  grouped: PropTypes.bool,
  /**
   * An alternate placeholder for the Search Box
   */
  searchBoxPlaceholder: PropTypes.string,
  /**
   * Props for the secondary link button underneath the SearchBox
   */
  secondaryLinkProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired
  }),
  /**
   * Custom text for the Clear Items button
   */
  clearButtonText: PropTypes.string,
  /**
   * Custom text for the Confirm button
   */
  confirmButtonText: PropTypes.string,
  /**
   * Control Confirm button disabled state
   */
  isConfirmButtonDisabled: PropTypes.bool,
  /**
   * Message to show when no items were passed
   */
  emptyStateMessage: PropTypes.string,
  /**
   * () => {}
   * Callback that's called when the empty state button is clicked, if not supplied - onCreateNew is used
   */
  onEmptyStateButtonClick: PropTypes.func,
  /**
   * () => {}
   * Title for empty state component
   */
  emptyStateTitle: PropTypes.string,
  /**
   * Button text to show when no items were found in the search. If provided - showing a button, else showing only notFoundTitle
   */
  notFoundMessage: PropTypes.string,
  /**
   * () => {}
   * Callback that's called when the Not Found State button is clicked, if not supplied - onCreateNew is used
   */
  onNotFoundButtonClick: PropTypes.func,
  /**
   * () => {}
   * Title for Not Found component
   */
  notFoundTitle: PropTypes.string,
  /**
   * Custom handler to filter search results. Returns filtered items.
   * (items, searchExpression) => [...]
   */
  filterOptions: PropTypes.func,
  /**
   * Custom props for the SearchBox. Note that any other props supplied will override searchBoxProps.
   */
  searchBoxProps: PropTypes.object,
  /**
   * Allows managing the searchExpression as a controlled value. Will chain it to any internal onChange behaviour
   */
  searchExpression: PropTypes.string,
  /**
   * Called when search expression has been changed
   */
  onSearchExpressionChange: PropTypes.func
};

export default withLocale(
  ListSelectionModal,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
